<template>
  <v-dialog :model-value="modelValue" width="1100px" @click:outside="closeModal">
    <BaseDialogCard v-if="modelValue" :title="tkey('title')" @close="closeModal">
      <v-container fluid class="cluster-switching__container">
        <v-row>
          <v-col id="cluster-switching-scheme-selection" class="pa-0 pr-6" cols="5">
            <h4 class="cluster-switching__heading mt-1 mb-4 pa-0 col-10">
              {{ tkey('schemeSelectionTitle') }}
            </h4>
            <div>
              <v-row>
                <v-col
                  v-for="(header, index) in headers"
                  :key="`header-${index}`"
                  :cols="header.cols"
                  :class="header.headerClass"
                >
                  <span>{{ header.text }}</span>
                </v-col>
              </v-row>
              <v-row v-for="(row, rowIndex) in schemes" :key="row._id" class="cluster-row">
                <v-col
                  v-for="header in headers"
                  :key="`cell-${row._id}-${header.id}`"
                  :cols="header.cols"
                  :class="header.colClass"
                  class="d-flex align-center"
                  style="padding: 6px"
                >
                  <template v-if="isFirstClusterCount1Entry(rowIndex, header, row)">
                    <v-checkbox
                      v-model="row.selected"
                      class="scheme-checkbox rtls-checkbox mx-2"
                      :disabled="true"
                    />
                  </template>
                  <template
                    v-if="
                      !isFirstClusterCount1Entry(rowIndex, header, row) &&
                      header.ui === CELL_TYPE.radio
                    "
                  >
                    <v-radio-group
                      v-model="selectedScheme"
                      hide-details
                      @update:model-value="onSchemeSelectedChanged"
                    >
                      <v-radio :ripple="false" :value="row._id" class="scheme-radio" />
                    </v-radio-group>
                  </template>
                  <template v-if="header.ui === CELL_TYPE.readonly">
                    <span>
                      {{ getClusterVariance(header.outputFormat, row[header.value]) }}
                    </span>
                  </template>
                </v-col>
              </v-row>
            </div>
          </v-col>

          <v-col cols="7">
            <v-row>
              <v-col>
                <div class="d-flex justify-space-between align-center mb-4">
                  <h4 class="cluster-switching__heading mt-1 mb-0 pa-0 col-8">
                    {{ tkey('calculationsTitle') }}
                    <DocsLink link="toolguide/100-clustering.html#running-clustered-switching" />
                  </h4>
                </div>
                <p class="cluster-switching__paragraph">
                  {{ tkey('calculationsSubtitle') }}
                </p>
              </v-col>
              <v-col>
                <h4 class="cluster-switching__heading mt-1 mb-4 pa-0 col-8">
                  {{ tkey('unclusterMatrixSelectionTitle') }}
                  <DocsLink link="toolguide/100-clustering.html#unclustered-switching" />
                </h4>
                <p class="cluster-switching__paragraph">
                  {{ tkey('unclusterMatrixSelection') }}
                </p>
              </v-col>
            </v-row>
            <v-row>
              <p
                v-if="!hasUnclusteredSwitchingMatrices"
                class="cluster-switching__paragraph font-italic d-inline-block invalid-rows-error-box"
              >
                {{ tkey('noUnclusteredMatrixFound') }}
              </p>
              <v-data-table
                disable-sort
                :headers="clusterConfigurationHeaders"
                :items="clustersData"
                hide-default-footer
                density="compact"
              >
                <template #item.calculationMethod="{ item }">
                  <v-radio-group v-model="item.calculationMethod" inline hide-details>
                    <v-radio
                      v-for="calculation in calculationTypes"
                      :key="calculation"
                      class="cluster-switching__label"
                      :ripple="false"
                      :label="tkey(`calculations.${calculation}`)"
                      :value="calculation"
                    />
                  </v-radio-group>
                </template>
                <template #item.usesClusteredSwitching="{ item }">
                  <v-checkbox
                    v-model="item.usesClusteredSwitching"
                    :disabled="!hasUnclusteredSwitchingMatrices"
                    :ripple="false"
                    class="scheme-checkbox rtls-checkbox mx-2"
                    hide-details
                  />
                </template>
              </v-data-table>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
      <template #footer>
        <PageActions
          :has-data-errors="hasDataErrors"
          :has-data-changes="true"
          :show-discard="false"
          save-btn-text="run"
          @save="onSave"
        >
          <template #right-btns>
            <v-btn class="text-outline" variant="text" @click="closeModal">
              {{ $t('actions.cancel') }}
            </v-btn>
          </template>
        </PageActions>

        <DependencyTreeFeedbackModal
          :value="dependencyTreeModalOpen"
          :results="dependencyTreeFeedback"
          page="clustering"
          @close="closeDependencyTreeModal"
          @commit="onSave(true)"
        />
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { find, get, isNil, keyBy, cloneDeep, pick } from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import calculationTypes from '@enums/clustering-calculation-types';
import TableEnum from '@enums/table/index';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useFormatNumber } from '@/composables/use-format-number';

// stores
import { useClusteringStore } from '@/stores/clustering';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useScenariosStore } from '@/stores/scenarios';
import { useSwitchingMatricesStore } from '@/stores/switching-matrices';

export default {
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close', 'process'],

  setup() {
    const { tkey } = useInternationalization('clusteringPage.switchingDialog');

    const { formatNumber } = useFormatNumber();

    return { tkey, formatNumber };
  },

  data() {
    return {
      CELL_TYPE: TableEnum.CELL_TYPE,
      OUTPUT_FORMAT: TableEnum.OUTPUT_FORMAT,
      calculationTypes,
      headers: [
        {
          id: 'selected',
          value: 'selected',
          text: null,
          cols: 1,
          ui: TableEnum.CELL_TYPE.radio,
        },
        {
          id: 'schemeName',
          value: 'name',
          ui: TableEnum.CELL_TYPE.readonly,
        },
        {
          id: 'schemeClusterCount',
          value: 'clusterCount',
          text: this.$t('clusteringPage.clusterCount'),
          cols: 2,
          headerClass: 'px-0',
          ui: TableEnum.CELL_TYPE.readonly,
        },
        {
          id: 'clusterVariance',
          value: 'variance',
          text: this.$t('clusteringPage.varianceExplained'),
          outputFormat: TableEnum.OUTPUT_FORMAT.percentage,
          cols: 2,
          headerClass: 'px-0',
          ui: TableEnum.CELL_TYPE.readonly,
        },
      ],
      selectedScheme: null,
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
      clusterConfigurationHeaders: [
        {
          title: this.$t('clusteringPage.clusterName'),
          value: 'clusterName',
          width: 40,
        },
        {
          title: this.tkey('calculationsTitle'),
          value: 'calculationMethod',
          width: 200,
        },
        {
          title: this.tkey('useClusteredSwitching'),
          value: 'usesClusteredSwitching',
          width: 20,
        },
      ],
      clustersData: [],
    };
  },

  computed: {
    ...mapState(useClusteringStore, ['getScenarioClusterSchemes']),
    ...mapState(useSwitchingMatricesStore, ['switchingMatricesUnclusteredCount']),

    hasDataErrors() {
      // check if nothing is selected
      return !this.selectedScheme;
    },

    firstUnclusteredScheme() {
      return this.getScenarioClusterSchemes
        .filter(s => !isNil(s.variance))
        .find((scheme, index) => {
          // all schemes are sorted by cluster count
          return this.isFirstClusterCount1Entry(index, { id: 'selected' }, scheme);
        });
    },

    schemes() {
      return this.getScenarioClusterSchemes
        .filter(s => !isNil(s.variance))
        .map(scheme => {
          return {
            ...scheme,
            selected: get(this.firstUnclusteredScheme, '_id') === scheme._id || scheme.selected,
          };
        });
    },

    selectedSchemeClusters() {
      if (!this.selectedScheme) return [];
      return get(
        find(this.getScenarioClusterSchemes, { _id: this.selectedScheme }),
        'clusters',
        []
      );
    },

    scenarioClusterSchemesMap() {
      return keyBy(this.getScenarioClusterSchemes, '_id');
    },

    hasUnclusteredSwitchingMatrices() {
      return this.switchingMatricesUnclusteredCount > 0;
    },
  },

  watch: {
    modelValue(isOpen, wasOpen) {
      if (!wasOpen && isOpen) {
        const selectedScheme = find(this.getScenarioClusterSchemes, { selected: true });
        this.selectedScheme = get(selectedScheme, '_id', null);
        this.onSchemeSelectedChanged(this.selectedScheme);
      }
    },
  },

  methods: {
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useScenariosStore, ['setScenarioHasSkippedClustering']),

    closeModal() {
      this.resetFormData();
      this.$emit('close');
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    async onSave(commit = false) {
      // Perform dependency tree check
      const results = await this.triggerDependencyTree({
        params: { change: 'clusteringModified', updates: {}, commit },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return;
      }

      await this.setScenarioHasSkippedClustering({ hasSkippedClustering: false });
      const selectedSchemes = [this.selectedScheme];
      // TODO: Cleanup this on AOV3-1809
      if (this.firstUnclusteredScheme) {
        selectedSchemes.push(this.firstUnclusteredScheme._id);
      }
      const clusterConfiguration = this.clustersData.map(c => {
        return {
          ...pick(c, ['clusterId', 'calculationMethod']),
          usesUnclusteredMatrix: !c.usesClusteredSwitching,
        };
      });

      const payload = { clusterSchemes: selectedSchemes, clusterConfiguration };
      this.$emit('process', payload);
      this.closeModal();
    },

    resetFormData() {
      this.selectedScheme = null;
    },

    getClusterVariance(headerType, value) {
      if (headerType === this.OUTPUT_FORMAT.percentage) {
        return value ? `${this.formatNumber({ number: value, format: 'percent' })}%` : 0;
      }
      return value;
    },

    isFirstClusterCount1Entry(rowIndex, header, row) {
      // schemes are sorted by cluster count, so if the first entry doesn't have cluster count of 1,
      // then there are no entries with cluster count as 1.
      return rowIndex === 0 && header.id === 'selected' && row.clusterCount === 1;
    },

    onSchemeSelectedChanged(selectedSchemeId) {
      const selectedScheme = this.scenarioClusterSchemesMap[selectedSchemeId];
      this.clustersData = cloneDeep(get(selectedScheme, 'clusters', [])).map(c => {
        const usesUnclusteredMatrix = isNil(c.usesUnclusteredMatrix)
          ? false
          : c.usesUnclusteredMatrix;
        return {
          ...c,
          calculationMethod: c.calculationMethod || calculationTypes.hybrid,
          usesUnclusteredMatrix,
          // the UI is for usage of Clustered Switching, so we need to invert the value
          usesClusteredSwitching: !usesUnclusteredMatrix,
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.cluster-switching {
  &__container {
    color: $assortment-text-colour;
    max-height: 500px;
    padding: 0;
  }

  &__heading {
    font-size: $panel-heading-size;
    line-height: $panel-heading-line-height;
  }

  &__paragraph {
    font-size: $panel-paragraph-size;
    line-height: $panel-paragraph-line-height;
  }
}

.cluster-row {
  background-color: $assortment-table-blue-bg-colour;
  margin-bottom: 5px;

  border-color: $assortment-table-blue-bg-colour;
  border-width: 10px;
  border-radius: 5px;
}

:deep(.scheme-checkbox) {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

:deep(.scheme-radio .v-icon.v-icon) {
  font-size: 18px;
}

:deep(.v-dialog footer.dialog-card__footer) {
  justify-content: space-between;
}

:deep(.cluster-switching__label label) {
  color: $assortment-text-colour;
  font-size: $panel-paragraph-size;
  line-height: $panel-paragraph-line-height;
}
</style>
